import { DEFAULT_AUTO_CLASS_CHANGE_OFFSET_MINUTES } from "utils/autoClassChangeConfig";
import {
  addMinutesToHHmm,
  formatTimeFromHHmm,
  parseTimeToMinutes,
} from "utils/dateTime";

export const AUTO_CLASS_CHANGE_MIGRATION_CHOICES = {
  SHIFT_AND_OFFSET: "shift_and_offset",
  KEEP_TIMES: "keep_times",
  SAME_TIMES: "same_times",
  CONFIGURE_LATER: "configure_later",
};

const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const getDefaultAutoClassChangeShiftMinutes = () => DEFAULT_AUTO_CLASS_CHANGE_OFFSET_MINUTES;

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const normalizeLegacyAutoClassChange = (autoClassChange) => {
  if (!isPlainObject(autoClassChange)) {
    return {
      active: true,
      offsetMinutesBeforeStart: 0,
      offsetSecondsBeforeStart: 0,
    };
  }

  return {
    active: autoClassChange.active !== false,
    offsetMinutesBeforeStart: Math.max(0, Number(autoClassChange.offsetMinutesBeforeStart) || 0),
    offsetSecondsBeforeStart: Math.max(0, Math.min(59, Number(autoClassChange.offsetSecondsBeforeStart) || 0)),
  };
};

const isPlaceholderTime = (startTime, endTime) =>
  startTime === "00:00" && endTime === "00:00";

export const isActiveRealScheduleEntry = (entry = {}) => {
  if (!entry?.active) return false;
  const startTime = typeof entry.startTime === "string" ? entry.startTime : "";
  const endTime = typeof entry.endTime === "string" ? entry.endTime : "";
  if (!startTime || !endTime || isPlaceholderTime(startTime, endTime)) return false;
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);
  return startMinutes !== null && endMinutes !== null && endMinutes > startMinutes;
};

const getActiveScheduleEntries = (schedule = {}) => {
  if (!isPlainObject(schedule)) return [];

  return Object.entries(schedule).flatMap(([letter, weekSchedule]) => {
    if (!isPlainObject(weekSchedule)) return [];

    return Object.entries(weekSchedule)
      .filter(([weekday]) => WEEKDAY_NAMES.includes(weekday))
      .filter(([, entry]) => isActiveRealScheduleEntry(entry))
      .map(([weekday, entry]) => ({
        letter,
        weekday,
        entry,
        startMinutes: parseTimeToMinutes(entry.startTime),
      }));
  });
};

export const hasActiveScheduleTimes = (schedule = {}) =>
  getActiveScheduleEntries(schedule).length > 0;

export const classNeedsAutoClassChangeMigration = (classItem = {}) => {
  if (classItem?.active === false) return false;
  const normalized = normalizeLegacyAutoClassChange(classItem?.autoClassChange);
  if (normalized.active === false) return false;
  if (normalized.offsetMinutesBeforeStart > 0 || normalized.offsetSecondsBeforeStart > 0) return false;
  return hasActiveScheduleTimes(classItem?.schedule);
};

export const needsAutoClassChangeMigration = (classes = []) =>
  (Array.isArray(classes) ? classes : []).some(classNeedsAutoClassChangeMigration);

const getClassLabel = (classItem = {}) =>
  String(classItem.className || classItem.period || classItem.name || "Class");

const getMigratableClasses = (classes = []) =>
  (Array.isArray(classes) ? classes : []).filter(classNeedsAutoClassChangeMigration);

const getEarliestSampleCandidates = (classes = []) =>
  getMigratableClasses(classes)
    .flatMap((classItem) =>
      getActiveScheduleEntries(classItem.schedule).map((scheduleEntry) => ({
        classItem,
        scheduleEntry,
      }))
    )
    .sort((first, second) => {
      if (first.scheduleEntry.startMinutes !== second.scheduleEntry.startMinutes) {
        return first.scheduleEntry.startMinutes - second.scheduleEntry.startMinutes;
      }
      return getClassLabel(first.classItem).localeCompare(getClassLabel(second.classItem));
    });

const getSampleCandidatesByClass = (classes = []) => {
  const earliestByClassId = new Map();

  getEarliestSampleCandidates(classes).forEach((candidate) => {
    const classId = String(candidate.classItem?.classId || getClassLabel(candidate.classItem));
    if (!earliestByClassId.has(classId)) {
      earliestByClassId.set(classId, candidate);
    }
  });

  return Array.from(earliestByClassId.values());
};

const buildSampleFromCandidate = ({
  candidate,
  choice,
  shiftMinutes,
  offsetMinutesBeforeStart,
  timeFormat,
}) => {
  if (!candidate) return null;

  const { classItem, scheduleEntry } = candidate;
  const { startTime, endTime } = scheduleEntry.entry;
  const effectiveOffsetMinutes = choice === AUTO_CLASS_CHANGE_MIGRATION_CHOICES.SAME_TIMES
    ? 0
    : offsetMinutesBeforeStart;
  const nextStartTime = choice === AUTO_CLASS_CHANGE_MIGRATION_CHOICES.SHIFT_AND_OFFSET
    ? addMinutesToHHmm(startTime, shiftMinutes)
    : startTime;
  const nextTransitionTime = addMinutesToHHmm(nextStartTime, -effectiveOffsetMinutes);
  const startDeltaMinutes = choice === AUTO_CLASS_CHANGE_MIGRATION_CHOICES.SHIFT_AND_OFFSET
    ? Math.max(0, Number(shiftMinutes) || 0)
    : 0;
  const oldStartMinutes = parseTimeToMinutes(startTime);
  const nextTransitionMinutes = parseTimeToMinutes(nextTransitionTime);
  const transitionDeltaMinutes =
    oldStartMinutes !== null && nextTransitionMinutes !== null
      ? nextTransitionMinutes - oldStartMinutes
      : 0;

  return {
    classId: classItem.classId || "",
    className: getClassLabel(classItem),
    letter: scheduleEntry.letter,
    weekday: scheduleEntry.weekday,
    before: {
      transitionTime: startTime,
      startTime,
      endTime,
      transitionLabel: formatTimeFromHHmm(startTime, timeFormat),
      startLabel: formatTimeFromHHmm(startTime, timeFormat),
      endLabel: formatTimeFromHHmm(endTime, timeFormat),
    },
    after: {
      transitionTime: nextTransitionTime,
      startTime: nextStartTime,
      endTime,
      transitionLabel: formatTimeFromHHmm(nextTransitionTime, timeFormat),
      startLabel: formatTimeFromHHmm(nextStartTime, timeFormat),
      endLabel: formatTimeFromHHmm(endTime, timeFormat),
      startDeltaMinutes,
      transitionDeltaMinutes,
    },
  };
};

const buildSamplePreviews = ({
  classes = [],
  choice,
  shiftMinutes,
  offsetMinutesBeforeStart,
  timeFormat,
}) =>
  getSampleCandidatesByClass(classes)
    .map((candidate) =>
      buildSampleFromCandidate({
        candidate,
        choice,
        shiftMinutes,
        offsetMinutesBeforeStart,
        timeFormat,
      })
    )
    .filter(Boolean);

export const buildAutoClassChangeMigrationPreview = ({
  classes = [],
  choice = AUTO_CLASS_CHANGE_MIGRATION_CHOICES.SHIFT_AND_OFFSET,
  shiftMinutes = getDefaultAutoClassChangeShiftMinutes(),
  offsetMinutesBeforeStart = shiftMinutes,
  timeFormat = "12h",
} = {}) => {
  const affectedClasses = getMigratableClasses(classes);
  const totalSlotCount = affectedClasses.reduce(
    (total, classItem) => total + getActiveScheduleEntries(classItem.schedule).length,
    0
  );
  const samples = buildSamplePreviews({
    classes,
    choice,
    shiftMinutes,
    offsetMinutesBeforeStart,
    timeFormat,
  });

  return {
    affectedClassCount: affectedClasses.length,
    totalSlotCount,
    samples,
    sample: samples[0] || null,
  };
};

const maybeShiftTime = (timeValue, shiftMinutes) => {
  if (typeof timeValue !== "string" || !timeValue || timeValue === "00:00") {
    return timeValue;
  }
  return addMinutesToHHmm(timeValue, shiftMinutes) || timeValue;
};

const buildShiftedSchedulePatch = ({ classItem, shiftMinutes }) => {
  const nextSchedule = {};
  const errors = [];
  let changedSlotCount = 0;

  Object.entries(classItem.schedule || {}).forEach(([letter, weekSchedule]) => {
    if (!isPlainObject(weekSchedule)) {
      nextSchedule[letter] = weekSchedule;
      return;
    }

    nextSchedule[letter] = {};
    Object.entries(weekSchedule).forEach(([weekday, entry]) => {
      if (!isActiveRealScheduleEntry(entry)) {
        nextSchedule[letter][weekday] = entry;
        return;
      }

      const originalStartMinutes = parseTimeToMinutes(entry.startTime);
      const shiftedStartMinutes = originalStartMinutes === null
        ? null
        : originalStartMinutes + shiftMinutes;
      const startTime = shiftedStartMinutes === null ? "" : addMinutesToHHmm(entry.startTime, shiftMinutes);
      const startMinutes = parseTimeToMinutes(startTime);
      const endMinutes = parseTimeToMinutes(entry.endTime);

      if (shiftedStartMinutes !== null && shiftedStartMinutes >= 1440) {
        errors.push(`${getClassLabel(classItem)} ${letter} ${weekday} would move to the next day after the time shift.`);
        nextSchedule[letter][weekday] = entry;
        return;
      }

      if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) {
        errors.push(`${getClassLabel(classItem)} ${letter} ${weekday} would start at or after its existing end time after the time shift.`);
        nextSchedule[letter][weekday] = entry;
        return;
      }

      changedSlotCount += 1;
      nextSchedule[letter][weekday] = {
        ...entry,
        startTime,
      };
    });
  });

  return { schedule: nextSchedule, errors, changedSlotCount };
};

const buildTopLevelTimePatch = ({ classItem, shiftMinutes }) => {
  const patch = {};
  if (hasOwn(classItem, "startTime")) {
    patch.startTime = maybeShiftTime(classItem.startTime, shiftMinutes);
  }
  if (hasOwn(classItem, "endTime")) {
    patch.endTime = classItem.endTime;
  }
  return patch;
};

export const buildAutoClassChangeMigrationPatches = ({
  classes = [],
  choice = AUTO_CLASS_CHANGE_MIGRATION_CHOICES.SHIFT_AND_OFFSET,
  shiftMinutes = getDefaultAutoClassChangeShiftMinutes(),
  offsetMinutesBeforeStart = shiftMinutes,
} = {}) => {
  if (choice === AUTO_CLASS_CHANGE_MIGRATION_CHOICES.CONFIGURE_LATER) {
    return { patches: [], errors: [] };
  }

  const patches = [];
  const errors = [];
  const normalizedShiftMinutes = Math.max(0, Number(shiftMinutes) || 0);
  const normalizedOffsetMinutes = choice === AUTO_CLASS_CHANGE_MIGRATION_CHOICES.SAME_TIMES
    ? 0
    : Math.max(0, Number(offsetMinutesBeforeStart) || 0);

  getMigratableClasses(classes).forEach((classItem) => {
    const patch = {
      autoClassChange: {
        active: true,
        offsetMinutesBeforeStart: normalizedOffsetMinutes,
        offsetSecondsBeforeStart: 0,
      },
    };
    let changedSlotCount = 0;

    if (choice === AUTO_CLASS_CHANGE_MIGRATION_CHOICES.SHIFT_AND_OFFSET) {
      const schedulePatch = buildShiftedSchedulePatch({
        classItem,
        shiftMinutes: normalizedShiftMinutes,
      });
      errors.push(...schedulePatch.errors);
      changedSlotCount = schedulePatch.changedSlotCount;
      patch.schedule = schedulePatch.schedule;
      Object.assign(patch, buildTopLevelTimePatch({ classItem, shiftMinutes: normalizedShiftMinutes }));
    }

    patches.push({
      classId: classItem.classId,
      classItem,
      patch,
      updatedClass: {
        ...classItem,
        ...patch,
      },
      changedSlotCount,
    });
  });

  return {
    patches: errors.length ? [] : patches,
    errors,
  };
};
